import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { compareDesc, compareAsc, parse } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { Spinner, Button, useTheme, Icon, Input } from '@iq/react-components';
import { ComtradeViewer } from '@iq/comtrade-viewer';
import '@iq/comtrade-viewer/dist/index.css';
import JSZip from 'jszip';
import { formatBytes, debounce, useClientSize, useBackgroundFreeze } from '../../../utils';
import { utcToSite, datetimeString } from '../../../datetimeUtils';
import { getPollingDateRange, getTimezone } from '../../../bundles/application';
import { useFilesSession, getFiles, requestFiles, downloadFiles } from '../../../bundles/files';
import {
  updateTfrTemplate,
  requestTfrTemplates,
  fetchTfrTemplates,
} from '../../../bundles/tfr-templates';
import { getComponentsLoaded } from '../../../bundles/components';
import {
  checkIsOnline,
  displayNotification,
  getNotification,
} from '../../../bundles/notifications';
import { getUserEventTypes } from '../../../bundles/events';
import services, { downloadZippedFiles } from '../../../services';
import { EVENT_TYPE_MACH } from '../../../constants';

import { useEventModal } from '../../EventModal';
import Empty from '../../Empty';
import Pagination from '../../Pagination';
import NewTemplateModal from './components/NewTemplateModal';
import Loader from '../../Loader';
import { DropdownMenu } from '../../DropdownMenu';
import Heading from '../../Heading';
import ListItem from '../../ListItem';
import LabelWithTooltip from '../../LabelWithTooltip';
import { Classification, ClassificationFilter } from './components';
import { filterMapping } from './constants';
import { getHasPermission } from '../../../bundles/auth/selectors';

const formatDuration = (samples, rate) => {
  const duration = samples / rate;
  if (duration > 0.5) {
    return `${Number.parseInt(duration, 10)} seconds`;
  }
  return `${duration * 1000} milliseconds`;
};

const CompactTFRList = ({
  loading,
  tfrs,
  onTFRSelect,
  filesPerPage,
  onTFRDownload,
  timezone,
  onSearch,
  search,
  compsLoaded,
  filesLoading,
  pages,
  page,
  setPage,
  isLeftPanel = false,
  selectedTfr,
  setSelectedTfr,
  format = 'widest',
  isFullScreen = false,
}) => {
  const renderEmpty = () => (
    <Empty
      text={search.length > 0 ? 'No records found' : 'No recordings available for this site'}
    />
  );

  const renderLoading = () => (
    <div className="tfr-list-loading">
      Loading recordings ... <Spinner size="s" />
    </div>
  );

  const renderList = () => (
    <div className="tfr-list">
      <div className="tfr-list-header">TFR File Name</div>
      {tfrs.map((tfr) => (
        <div
          key={tfr.id}
          className={`tfr-item ${selectedTfr === tfr.id ? 'selected-tfr' : ''}`}
          onClick={() => {
            onTFRSelect(tfr, 'tfr-view');
            setSelectedTfr(tfr.id);
          }}
        >
          {tfr.filename.replace('.zip', '')}
        </div>
      ))}
    </div>
  );

  const renderContent = () => {
    if (tfrs.length === 0) {
      return renderEmpty();
    }

    if (!compsLoaded || filesLoading) {
      return renderLoading();
    }
    return isLeftPanel ? (
      renderList()
    ) : (
      <RenderTable
        tfrs={tfrs}
        filesPerPage={filesPerPage}
        format={format}
        isFullScreen={isFullScreen}
        onTFRDownload={onTFRDownload}
        onTFRSelect={onTFRSelect}
        loading={loading}
        timezone={timezone}
      />
    );
  };

  return (
    <>
      <div className="tfr-online-selector">
        <div className={`tfr-online-selector--heading ${format === 'narrow' ? 'narrow' : ''}`}>
          {!isLeftPanel && <h4 className="tfr-list-title">Recordings</h4>}
          <Input
            type="text"
            onChange={onSearch}
            value={search}
            placeholder="Search TFR file name..."
          />
        </div>
        {renderContent()}
      </div>
      <Pagination
        pages={pages}
        page={page}
        setPage={setPage}
      />
    </>
  );
};

const RenderTable = ({
  tfrs,
  filesPerPage,
  format,
  isFullScreen,
  onTFRDownload,
  onTFRSelect,
  loading,
  timezone,
}) => (
  <table>
    <thead>
      <tr>
        {(format !== 'narrow' || isFullScreen) && <th className="date">Date</th>}
        <th className="name">Name</th>
        {(format !== 'narrow' || isFullScreen) && (
          <>
            <th className="duration">Duration</th>
            <th className="rate">Sample rate</th>
          </>
        )}
        {(format === 'widest' || isFullScreen) && (
          <th
            className="size"
            colSpan="2"
          >
            Size
          </th>
        )}
      </tr>
    </thead>
    <tbody>
      {tfrs.slice(0, filesPerPage).map((file) => {
        const { filename, size, attributes: { ts, sampleRates = [] } = {} } = file;
        const rate = sampleRates[0]?.rate || 0;
        const samples = sampleRates[0]?.end || 0;
        const duration = formatDuration(samples, rate);

        return (
          <tr key={`file-${file.id}`}>
            {(format !== 'narrow' || isFullScreen) && (
              <td className="date">{datetimeString(utcToSite(ts, timezone), timezone)}</td>
            )}
            <td className="name">{filename.replace('.zip', '')}</td>
            {(format !== 'narrow' || isFullScreen) && (
              <>
                <td className="duration">{duration}</td>
                <td className="rate">{rate} Hz</td>
              </>
            )}

            {format !== 'widest' && !isFullScreen ? (
              <td className="dropdown-wrapper">
                <DropdownMenu
                  trigger={
                    <Button
                      design="text"
                      tooltip="More..."
                    >
                      <Icon
                        style={{ cursor: 'pointer', color: '#8e8e8e' }}
                        icon="he-moreoptionsvertical"
                      />
                    </Button>
                  }
                  menuOptions={[
                    {
                      label: 'Download',
                      value: 'download',
                      onSelect: () => onTFRDownload(file.id, filename),
                    },
                    {
                      label: 'View',
                      value: 'view',
                      onSelect: () => onTFRSelect(file, 'tfr-list'),
                    },
                  ]}
                  menuXPlacement="left"
                  styles={{
                    dropdownIndicator: { filter: 'none', padding: 'calc(0.15rem + 2.5px)' },
                    control: { minHeight: 'unset' },
                  }}
                />
              </td>
            ) : (
              <>
                <td className="size">{formatBytes(size)}</td>
                <td className="view">
                  <Button
                    onClick={() => onTFRDownload(file.id, filename)}
                    disabled={loading !== null}
                    tooltip="Download"
                    activity="secondary"
                  >
                    Download
                  </Button>
                  <Button
                    disabled={loading !== null}
                    onClick={() => onTFRSelect(file, 'tfr-list')}
                    className="view-button"
                  >
                    {loading === file.id ? <Spinner size="s" /> : 'View'}
                  </Button>
                </td>
              </>
            )}
          </tr>
        );
      })}
    </tbody>
  </table>
);

const SelectFiles = ({
  configFile,
  dataFile,
  eventsFile,
  onConfigSelect,
  onDataSelect,
  onEventsSelect,
  onIgnoreEvents,
}) => (
  <div className="select-files-component">
    {!configFile && (
      <label className="select-files--field">
        <div>Load configuration file</div>
        <input
          className="trf-viewer--input"
          disabled={configFile !== null}
          type="file"
          name="file"
          id="file"
          accept=".cfg"
          onChange={onConfigSelect}
        />
      </label>
    )}
    {Boolean(configFile) && !dataFile && (
      <label className="select-files--field">
        <div>Load data file</div>
        <input
          className="trf-viewer--input"
          disabled={dataFile !== null}
          type="file"
          name="file"
          id="file"
          accept=".dat"
          onChange={onDataSelect}
        />
      </label>
    )}
    {Boolean(dataFile) && (
      <>
        <label className="select-files--field">
          <div>Load events file</div>
          <input
            className="trf-viewer--input"
            disabled={eventsFile !== null}
            type="file"
            name="file"
            id="file"
            accept=".xml"
            onChange={onEventsSelect}
          />
        </label>
        <Button
          onClick={onIgnoreEvents}
          activity="secondary"
        >
          Get Events from API
        </Button>
      </>
    )}
  </div>
);

const ComtradeContainer = React.memo(
  ({
    configFile,
    tfrs,
    dataFile,
    eventsFile,
    getEventsCallback,
    onOpenEvent,
    onFormReset,
    theme,
    tz,
    site,
    onSearch,
    search,
    onTFRSelect,
    selectedTfrId,
    compsLoaded,
    filesLoading,
    pages,
    page,
    setPage,
    loading,
    onTFRDownload,
    timezone,
    loadTfr,
  }) => {
    const dispatch = useDispatch();
    const tfrTemplates = useSelector(fetchTfrTemplates);
    const [showNewTemplate, setShowNewTemplate] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [templateData, setTemplateData] = useState({});
    const [showNav, setShowNav] = useState(false);
    const [dataFileToViewer, setDataFileToViewer] = useState({});
    const [selectedTfr, setSelectedTfr] = useState(selectedTfrId);
    useBackgroundFreeze();

    useEffect(() => {
      setDataFileToViewer(dataFile);
    }, [dataFile]);

    const onCloseModal = useCallback(() => {
      setShowNewTemplate(false);
    });

    const canDeleteTemplate = useSelector((state) =>
      getHasPermission(state, 'tfr-templates/Delete', { org: site.org, site: site.id })
    );

    const onTemplateActions = useCallback((action) => {
      setTemplateData(action);
      if (action.actionType === 'save') {
        setShowLoader(true);
        const data = {
          site: site.id,
          name: action.name,
          value: { template: action.template },
          org: site.org,
          templateType: 'tfr',
        };
        dispatch(
          updateTfrTemplate(action.id, data, () => {
            setShowLoader(false);
          })
        );
      } else {
        setShowNewTemplate(true);
      }
    }, []);

    return (
      <div className="tfr-view">
        <div className={`tfr-sidebar${showNav ? ' show' : ''}`}>
          <div className={`tfr-sidebar-header${showNav ? ' show' : ''}`}>
            <Icon
              size={'s'}
              icon={'he-list'}
              onClick={() => setShowNav(true)}
            />
            <Icon
              size={'xs'}
              icon={'he-backwards'}
              onClick={() => setShowNav(false)}
            />
          </div>
          <div className={`tfr-sidebar-body${showNav ? ' show' : ''}`}>
            <CompactTFRList
              tfrs={tfrs}
              onTFRSelect={onTFRSelect}
              filesPerPage={2}
              loading={loading}
              onTFRDownload={onTFRDownload}
              timezone={timezone}
              onSearch={onSearch}
              search={search}
              compsLoaded={compsLoaded}
              filesLoading={filesLoading}
              pages={pages}
              page={page}
              setPage={setPage}
              selectedTfr={selectedTfr}
              setSelectedTfr={setSelectedTfr}
              isLeftPanel={true}
            />
          </div>
        </div>
        <div className={`tfr-view-details${showNav ? ' show' : ''}`}>
          {(loadTfr || showLoader) && (
            <div className="tfr-page-loader">
              <Loader
                text={loadTfr ? 'Loading TFR File...' : ''}
                overlay
              />
            </div>
          )}

          <ComtradeViewer
            configFile={configFile}
            dataFile={dataFileToViewer}
            eventsFile={eventsFile}
            theme={theme}
            getEventsCallback={getEventsCallback}
            selectEventCallback={onOpenEvent}
            onTemplateActions={onTemplateActions}
            templateList={tfrTemplates}
            tz={tz}
            show={showNav}
            canDeleteTemplate={canDeleteTemplate}
          >
            <Button
              icon={<Icon icon="back" />}
              onClick={onFormReset}
              activity="secondary"
              slim
            >
              Close
            </Button>
          </ComtradeViewer>

          {showNewTemplate && (
            <NewTemplateModal
              templateData={templateData}
              onCloseModal={onCloseModal}
              tfrTemplates={tfrTemplates}
              site={site}
              setShowLoader={setShowLoader}
            />
          )}
        </div>
      </div>
    );
  }
);

// Update headers to be responsive too
const getResponsiveHeaders = (format, isFullScreen) => {
  const headers = [{}]; // Select column

  if (format !== 'narrow' || isFullScreen) {
    headers.push({ sortKey: '', label: 'Date' });
  }

  headers.push({ sortKey: 'filename', label: 'Name' });

  if (format !== 'narrow' || isFullScreen) {
    headers.push({ label: 'Classification' }, { label: 'Duration' }, { label: 'Sample Rate' });
  }

  if (format === 'widest' || isFullScreen) {
    headers.push({ sortKey: 'size', label: 'Size' });
  }

  return headers;
};

const getResponsiveColumnWidths = (format, isFullScreen) => {
  if (format === 'narrow' && !isFullScreen) {
    return {
      type: 'percent',
      widths: ['-', 100, '-'], // Select, Name, Actions
    };
  } else if (format === 'widest' || isFullScreen) {
    return {
      type: 'percent',
      widths: ['-', 20, 30, 15, 15, 10, 10, '-'], // All columns
    };
  } else {
    // Medium format (not narrow, not widest)
    return {
      type: 'percent',
      widths: ['-', 25, 35, 15, 15, 10, '-'], // Select, Date, Name, Classification, Duration, Sample Rate, Actions
    };
  }
};

const TFRList = React.memo(
  ({
    format,
    isFullScreen,
    sortBy,
    handleSort,
    comtradeFiles,
    allSelected,
    selectedIds,
    search,
    searchInput,
    filesLoading,
    isInitializing,
    selectedFilters,
    pages,
    page,
    clientReference,
    toggleSelectAllVariables,
    onDownloadSelected,
    onSearch,
    setSelectedFilters,
    setPage,
    getResponsiveListItemColumns,
    onTFRSelect,
    onTFRDownload,
  }) => {
    const responsiveHeaders = getResponsiveHeaders(format, isFullScreen);
    const responsiveColumnWidths = getResponsiveColumnWidths(format, isFullScreen);

    const responsiveHeaderColumns = responsiveHeaders.map((header, index) => {
      const key = `${header.sortKey || header.label}-${index}`;
      if (!Object.prototype.hasOwnProperty.call(header, 'sortKey')) {
        return <div key={key}>{header.label}</div>;
      }
      const icon = sortBy.sortKey === header.sortKey && sortBy.desc ? 'he-down' : 'he-up';
      return (
        <Button
          key={key}
          onClick={() => handleSort(header.sortKey)}
          icon={
            <Icon
              icon={icon}
              size="xs"
            />
          }
          iconPosition="right"
          activity="secondary"
        >
          {header.label}
        </Button>
      );
    });

    return (
      <div
        ref={clientReference}
        className="tfr-body-container"
      >
        <div className="tfr-list-header">
          <div className="tfr-selection-controls">
            {comtradeFiles?.length > 0 && (
              <label className="tfr-select-all-wrapper">
                <Icon
                  className={`tfr-select-all-checkbox ${allSelected ? 'checked' : ''}`}
                  icon={`${allSelected ? 'he-checkbox-selected' : 'he-checkbox'}`}
                  size="s"
                  onClick={toggleSelectAllVariables}
                />
                Select all
              </label>
            )}
            {selectedIds.length > 0 && (
              <Button
                onClick={onDownloadSelected}
                activity="secondary"
                icon={<Icon icon="he-download" />}
                tooltip={`Download ${selectedIds.length} selected recording${selectedIds.length > 1 ? 's' : ''}`}
                slim={true}
              />
            )}
          </div>
          <div className="tfr-filters">
            <ClassificationFilter
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
            />
            <Input
              type="text"
              onChange={onSearch}
              value={searchInput}
              placeholder="Search TFR file name..."
            />
          </div>
        </div>
        <div className="list-container">
          <ListItem
            isHeader
            columns={responsiveHeaderColumns}
            columnWidths={responsiveColumnWidths}
            withSelect
          />
          <div className="custom-thin-scrollbar">
            {filesLoading || isInitializing ? (
              <div className="tfr-list-loading">
                <Spinner size="s" />
              </div>
            ) : comtradeFiles?.length === 0 ? (
              <div className="tfr-list-no-recordings">
                <Empty
                  text={
                    search ? 'No recordings found matching your search' : 'No recordings available'
                  }
                />
              </div>
            ) : (
              comtradeFiles?.map((file) => (
                <ListItem
                  key={file.id}
                  entity={`Signal (${file.name})`}
                  item={file}
                  columns={getResponsiveListItemColumns(file, format, isFullScreen)}
                  columnWidths={responsiveColumnWidths}
                  withSelect
                  disableDelete
                  disableEdit
                  extraMenuActions={[
                    {
                      placement: 'before',
                      text: 'View',
                      action: () => {
                        onTFRSelect(file, 'tfr-list');
                      },
                    },
                    {
                      placement: 'before',
                      text: 'Download',
                      action: () => {
                        onTFRDownload(file.id, file.filename);
                      },
                    },
                  ]}
                />
              ))
            )}
          </div>
        </div>
        <Pagination
          pages={pages}
          page={page}
          setPage={setPage}
        />
      </div>
    );
  }
);

const TfrPanel = ({ site, canLoadLocal = false, filesPerPage = 10, format, isFullScreen }) => {
  const dispatch = useDispatch();
  const sessionId = useFilesSession();
  const theme = useTheme();
  const {
    files: allFiles,
    pages,
    loading: filesLoading,
  } = useSelector((state) => getFiles(state, sessionId));
  const timezone = useSelector(getTimezone);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [loadTfr, setLoadTfr] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [sortBy, setSortBy] = useState({ sortKey: '', desc: true });

  const [_, clientReference] = useClientSize();

  useEffect(() => {
    dispatch(requestTfrTemplates({ site: site.id, templateType: 'tfr' }));
  }, [site.id, dispatch]);

  const query = useMemo(() => {
    const q = {
      org: site.org,
      site: site.id,
      fileType: 'comtrade',
      limit: filesPerPage,
      order: sortBy.desc ? 'desc' : 'asc',
      sortBy: sortBy.sortKey,
      page,
    };

    if (selectedFilters.length > 0 && selectedFilters.length < Object.keys(filterMapping).length) {
      q.tfrClassification = selectedFilters;
    }

    if (search) {
      q.search = search;
    }

    return q;
  }, [page, search, filesPerPage, sortBy, selectedFilters, site.id, site.org]);

  useEffect(() => {
    dispatch(requestFiles(sessionId, query));
  }, [query, dispatch, sessionId]);

  useEffect(() => {
    setPage(1);
  }, [search, selectedFilters]);

  const debouncedSetSearch = useMemo(() => debounce((value) => setSearch(value), 300), []);

  const onSearch = (event) => {
    const value = event.target.value;
    setSearchInput(value);
    debouncedSetSearch(value);
  };

  const compsLoaded = useSelector(getComponentsLoaded);
  const { startDate, endDate } = useSelector(getPollingDateRange);

  const parseTimeString = (timeString, revision, defaultTs) => {
    const dateFormats = {
      1991: 'MM/dd/yy,HH:mm:ss.SSSSSS',
      1999: 'd/M/yyyy,HH:mm:ss.SSSSSS',
      2013: 'd/M/yyyy,HH:mm:ss.SSSSSS',
    };

    let ts = defaultTs;
    const date = parse(timeString, dateFormats[revision], new Date());

    if (!Number.isNaN(date.getTime())) {
      ts = fromZonedTime(date, timezone).getTime();
    }

    return ts;
  };

  const comtradeFiles = useMemo(
    () =>
      (allFiles || [])
        .map((file) => {
          const {
            attributes: { time, revision },
          } = file;
          let ts = time;
          if (typeof time === 'string') {
            ts = parseTimeString(time, revision, file.createdAt);
          }
          if (time === undefined) {
            ts = file.createdAt;
          }
          return {
            ...file,
            attributes: {
              ...file.attributes,
              ts,
            },
          };
        })
        .toSorted(
          sortBy.sortKey === ''
            ? ({ attributes: { ts: tsA } }, { attributes: { ts: tsB } }) =>
                sortBy.desc
                  ? compareDesc(new Date(tsA), new Date(tsB))
                  : compareAsc(new Date(tsA), new Date(tsB))
            : () => 0
        ),
    [allFiles, startDate, endDate, sortBy]
  );

  const [configFile, setConfigFile] = useState(null);
  const [dataFile, setDataFile] = useState(null);
  const [eventsFile, setEventsFile] = useState(null);
  const [ignoreEvents, setIgnoreEvents] = useState(false);
  const [loading, setLoading] = useState(null);
  const [selectedTfrId, setSelectedTfrId] = useState('');

  const onConfigSelect = async (event) => {
    if (event.target.files) {
      setConfigFile(event.target.files[0]);
    }
  };

  const onDataSelect = async (event) => {
    if (event.target.files) {
      setDataFile(event.target.files[0]);
    }
  };

  const onEventsSelect = async (event) => {
    if (event.target.files) {
      setEventsFile(event.target.files[0]);
    }
  };

  const onTFRSelect = useCallback(async (file, pageType) => {
    const { id } = file;
    setLoading(id);
    setSelectedTfrId(id);
    setLoadTfr(true);

    try {
      const blob = await downloadZippedFiles({ file: [id], org: site?.org, site: site?.id });
      const zipBuffer = await blob.arrayBuffer();
      const outerArchive = await JSZip.loadAsync(zipBuffer);

      const innerZipEntry = Object.values(outerArchive.files).find((f) =>
        f.name.toLowerCase().endsWith('.zip')
      );

      if (!innerZipEntry) {
        throw new Error('No inner ZIP file found in archive');
      }

      const innerZipBuffer = await innerZipEntry.async('arraybuffer');
      const archive = await JSZip.loadAsync(innerZipBuffer);

      const files = Object.values(archive.files).reduce((accumulator, compressedFile) => {
        const fileName = compressedFile.name.toLowerCase();

        if (!accumulator.cfgFile && !fileName.includes('__macosx') && fileName.endsWith('.cfg')) {
          accumulator.cfgFile = compressedFile;
        }
        if (!accumulator.datFile && !fileName.includes('__macosx') && fileName.endsWith('.dat')) {
          accumulator.datFile = compressedFile;
        }
        if (
          !accumulator.eventsFile &&
          !fileName.includes('__macosx') &&
          fileName.endsWith('.xml')
        ) {
          accumulator.eventsFile = compressedFile;
        }
        return accumulator;
      }, {});

      if (!files.cfgFile || !files.datFile) {
        // TODO: we should notify the user
        setLoading(null);
        throw new Error('COMTRADE archive was missing .cfg or .dat file');
      }

      const cfgData = await files.cfgFile.async('blob');
      const datData = await files.datFile.async('blob');
      const cfgFile = new File([cfgData], files.cfgFile.name);
      const datFile = new File([datData], files.datFile.name);

      if (files.eventsFile) {
        const eventsData = await files.eventsFile.async('blob');
        const evtsFile = new File([eventsData], files.eventsFile.name);
        setEventsFile(evtsFile);
      } else {
        setIgnoreEvents(true);
      }

      setConfigFile(cfgFile);
      setDataFile(datFile);
    } catch (error) {
      console.error('Error loading TFR archive:', error);
    } finally {
      setLoading(null);
      setLoadTfr(false);
    }
  }, []);

  const onFormReset = () => {
    setConfigFile(null);
    setDataFile(null);
    setEventsFile(null);
    setIgnoreEvents(false);
  };

  const getEvents = useCallback(
    async ({ from, to }) => {
      const events = await services.data
        .getEventBuckets({
          site: site.id,
          from,
          to,
          interval: Math.round((to - from) / 400), // 400 event buckets
          aggregationType: 'time_severity_lists',
        })
        .then(
          (response) => response,
          (error) => {
            console.error('Unable to fetch iot events:', error);
            dispatch(checkIsOnline());
            dispatch(displayNotification(getNotification('getIotEvents', 'error')()));
            return { values: [] };
          }
        );
      return events.values;
    },
    [site]
  );

  const [eventDetail, onEventDetail] = useEventModal();

  const userEventTypes = useSelector(getUserEventTypes);
  const onOpenEvent = useCallback(
    (event) => {
      // Guards against an unresolved viewer event click (e.g. an aggregate count row)
      if (!event) {
        return;
      }

      onEventDetail({
        modalType: EVENT_TYPE_MACH,
        event,
        eventId: event.id,
        userEventTypes,
        site,
      });
    },
    [userEventTypes, site, onEventDetail]
  );

  const onTFRDownload = (id, filename) => {
    dispatch(
      downloadFiles({
        id,
        filename,
      })
    );
  };

  const onDownloadSelected = useCallback(() => {
    if (selectedFiles.length > 1) {
      dispatch(downloadFiles(selectedFiles, 'recordings.zip'));
    } else if (selectedFiles.length === 1) {
      dispatch(downloadFiles(selectedFiles[0]));
    }
  }, [selectedFiles, dispatch]);

  const onToggleSelection = useCallback(
    (id) => {
      const isSelected = selectedIds.includes(id);
      if (isSelected) {
        setSelectedIds((previous) => previous.filter((selectedId) => selectedId !== id));
        setSelectedFiles((previous) => previous.filter((file) => file.id !== id));
      } else {
        const fileToAdd = comtradeFiles.find((file) => file.id === id);
        setSelectedIds((previous) => [...previous, id]);
        setSelectedFiles((previous) => [...previous, fileToAdd]);
      }
    },
    [selectedIds, comtradeFiles]
  );

  const toggleSelectAllVariables = useCallback(
    (event) => {
      event.stopPropagation();
      if (selectedIds.length === comtradeFiles.length) {
        const currentPageIds = new Set(comtradeFiles.map((f) => f.id));
        setSelectedIds((previous) => previous.filter((id) => !currentPageIds.has(id)));
        setSelectedFiles((previous) => previous.filter((file) => !currentPageIds.has(file.id)));
      } else {
        const newIds = comtradeFiles.map((f) => f.id);
        const filesToAdd = comtradeFiles.filter((f) => !selectedIds.includes(f.id));
        setSelectedIds((previous) => [...new Set([...previous, ...newIds])]);
        setSelectedFiles((previous) => [...previous, ...filesToAdd]);
      }
    },
    [comtradeFiles, selectedIds]
  );

  const handleSort = (nextKey) => {
    setSortBy((previous) => ({
      sortKey: nextKey,
      desc: previous.sortKey === nextKey ? !previous.desc : true,
    }));
  };

  const allSelected = comtradeFiles?.length && comtradeFiles?.length === selectedIds.length;

  const getResponsiveListItemColumns = useCallback(
    (comtrade, format, isFullScreen) => {
      const isSelected = selectedIds.includes(comtrade.id);
      const { filename, size, attributes: { ts, sampleRates = [] } = {} } = comtrade;
      const rate = sampleRates[0]?.rate || 0;
      const samples = sampleRates[0]?.end || 0;
      const duration = formatDuration(samples, rate);
      const classification = comtrade?.tfr_classification?.classification;

      const handleDoubleClick = () => onTFRSelect(comtrade, 'tfr-list');

      const cols = [
        // Select column - always present
        <div
          key={`${comtrade.id}-col-1`}
          className="table-cell select-column"
        >
          <Button design="text">
            <Icon
              className={`checkbox ${isSelected ? 'checked' : ''}`}
              icon={`${isSelected ? 'he-checkbox-selected' : 'he-checkbox'}`}
              size="s"
              onClick={(event) => {
                event.stopPropagation();
                onToggleSelection(comtrade.id);
              }}
            />
          </Button>
        </div>,
      ];

      // Date column - hidden in narrow unless fullscreen
      if (format !== 'narrow' || isFullScreen) {
        cols.push(
          <div
            key={`${comtrade.id}-col-2`}
            className="ellipsed-text"
          >
            {datetimeString(utcToSite(ts, timezone), timezone)}
          </div>
        );
      }

      // Name column - always present
      cols.push(
        <div
          key={`${comtrade.id}-col-3`}
          className="ellipsed-text"
          onDoubleClick={handleDoubleClick}
        >
          <LabelWithTooltip name={filename.replace('.zip', '')} />
        </div>
      );

      // Classification, Duration, Sample Rate - hidden in narrow unless fullscreen
      if (format !== 'narrow' || isFullScreen) {
        cols.push(
          <div
            key={`${comtrade.id}-col-4`}
            className="ellipsed-text"
          >
            {classification && <Classification classification={classification} />}
          </div>,
          <div
            key={`${comtrade.id}-col-5`}
            className="ellipsed-text"
          >
            {duration}
          </div>,
          <div
            key={`${comtrade.id}-col-6`}
            className="blocked-item-container"
          >
            {rate} Hz
          </div>
        );
      }

      // Size - only in widest/fullscreen
      if (format === 'widest' || isFullScreen) {
        cols.push(
          <div
            key={`${comtrade.id}-col-7`}
            className="blocked-item-container"
          >
            {formatBytes(size)}
          </div>
        );
      }

      return cols;
    },
    [selectedIds, onTFRSelect, onToggleSelection, timezone]
  );

  if (configFile && dataFile && (eventsFile || ignoreEvents)) {
    return (
      <>
        <ComtradeContainer
          configFile={configFile}
          tfrs={comtradeFiles}
          dataFile={dataFile}
          eventsFile={eventsFile}
          getEventsCallback={getEvents}
          onOpenEvent={onOpenEvent}
          onFormReset={onFormReset}
          theme={theme}
          tz={timezone}
          site={site}
          onSearch={onSearch}
          search={search}
          onTFRSelect={onTFRSelect}
          selectedTfrId={selectedTfrId}
          compsLoaded={compsLoaded}
          filesLoading={filesLoading}
          pages={pages}
          page={page}
          setPage={setPage}
          loading={loading}
          onTFRDownload={onTFRDownload}
          timezone={timezone}
          loadTfr={loadTfr}
        />
        {eventDetail}
      </>
    );
  }

  return (
    <div className="tfr-panel-component">
      {loadTfr && (
        <div className="tfr-page-loader">
          <Loader
            text="Loading TFR File..."
            overlay
          />
        </div>
      )}
      {canLoadLocal && (
        <div className="tfr-local-selector">
          <h4 className="tfr-list-title">Select local files</h4>
          <SelectFiles
            configFile={configFile}
            dataFile={dataFile}
            eventsFile={eventsFile}
            ignoreEvents={ignoreEvents}
            onConfigSelect={onConfigSelect}
            onDataSelect={onDataSelect}
            onEventsSelect={onEventsSelect}
            onIgnoreEvents={() => setIgnoreEvents(true)}
          />
        </div>
      )}
      <Heading
        contentLeft={
          <div>
            <div className="tfr-list-title">Recordings</div>
          </div>
        }
        contentRight={''}
      />
      <TFRList
        format={format}
        isFullScreen={isFullScreen}
        sortBy={sortBy}
        handleSort={handleSort}
        comtradeFiles={comtradeFiles}
        allSelected={allSelected}
        selectedIds={selectedIds}
        search={search}
        searchInput={searchInput}
        filesLoading={filesLoading}
        isInitializing={allFiles === null}
        selectedFilters={selectedFilters}
        pages={pages}
        page={page}
        clientReference={clientReference}
        toggleSelectAllVariables={toggleSelectAllVariables}
        onDownloadSelected={onDownloadSelected}
        onSearch={onSearch}
        setSelectedFilters={setSelectedFilters}
        setPage={setPage}
        getResponsiveListItemColumns={getResponsiveListItemColumns}
        onTFRSelect={onTFRSelect}
        onTFRDownload={onTFRDownload}
      />
    </div>
  );
};

export default TfrPanel;
